<template>
  <div
    class="relative"
    :class="{
      'pb-48': isNotStartPage,
    }"
  >
    <div class="container xxl:px-24 md:px-56 px-0">
      <div class="w-full flex flex-col md:flex-row md:flex-wrap">
        <div class="md:order-2 flexBasisHalf md:pr-12">
          <div class="md:hidden">
            <img 
              v-if="document.images.length === 1" 
              :src="document.images[0].url" 
              :alt="document.images[0].alt"
              class="md:rounded-regular" 
            >
            <div 
              v-else-if="document.images.length > 1" 
              class="swiper-container overflow-hidden flex justify-center"
            >
              <div class="swiper-wrapper">
                <div 
                  v-for="(image, imageIndex) in document.images"
                  :key="'product-spot-image-' + imageIndex + image.url" 
                  class="swiper-slide"
                >
                  <div class="block h-full w-full">
                    <img 
                      :src="imgix(image.url)" 
                      :alt="image.caption" 
                      class="md:rounded-regular" 
                    >
                  </div>
                </div>
              </div>
              <div class="swiper-button-next right-16 text-xl">
                <fa class="text-blue-75" :icon="['far', 'angle-right']" />
              </div>
              <div class="swiper-button-prev left-16 text-xl">
                <fa class="text-blue-75" :icon="['far', 'angle-left']" />
              </div>
              <div class="swiper-pagination bottom-0" />
            </div>
          </div>
          <div 
            class="hidden md:block" 
            :class="{'md:grid desktopImagesMulti': document.images.length > 2,}"
          >
            <img 
              v-for="(image, index) in document.images" :key="image.url + index" 
              :src="image.url" 
              :alt="image.alt"
              class="md:rounded-regular w-full h-full object-cover" 
              :class="[
                `image${index + 1}`,
                { 'mb-24': document.images.length <= 2 },
              ]" 
            >
          </div>
        </div>
        <div class="md:order-1 flexBasisFull">
          <StlBreadcrumbs v-if="!uiStore.isMobile" />
        </div>
        <div class="md:order-3 flexBasisHalf px-24 md:pl-12 md:pr-0">
          <div v-for="product in products" :key="product.sku" class="border-b border-border pb-24 mb-24">
            <BuyProduct 
              :product="product"
              design="Look" 
              @on-item-change="onItemChange"
            />
          </div>
        </div>
      </div>
    </div>
    <div>
      <div class="w-full overflow-hidden sm:pt-32">
        <component 
          :is="components[component.layout]" 
          v-for="(component, idx) in document.components"
          :key="component.layout + idx" v-bind="{ component }" 
          :has-side-bar="hasSideBar"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import SimpleSwiper from '~/components/global/SimpleSwiper.vue';
import BlockComponents from '../../constants/block-components';
import BuyProduct from '~/components/productpage/BuyProduct.vue';
import Page from '~/models/page';
import type { EcomProduct } from '~/constants/types/product';
import Product from '~/models/product';
import { useConfigStore } from '~/store/config';
import StlBreadcrumbs from '~/components/shop-the-look/StlBreadcrumbs.vue';
import { useUiStore } from '~/store/ui';
import ProductSizeGuide from '~/components/productpage/ProductSizeGuide.vue';
import ProductStockNotification from '~/components/productpage/ProductStockNotification.vue';
const { fetchProducts } = useProductService();

const config = useConfigStore();
const route = useRoute();
const uiStore = useUiStore();

const props = defineProps<{
  page: {
    type: Object,
    required: true,
  },
}>();

const isNotStartPage = computed(()=>{
  return !config.startPageUrls.includes(
    `${process.env.FRONTEND_URL}${route.fullPath}`
  );
});

const links = computed(()=>{
  return props.page.sidebar_links ? props.page.sidebar_links : [];
});

const hasSideBar = computed(()=>{
  return links.value.length > 0;
});

const components = computed<typeof BlockComponents>(()=> {
  return BlockComponents;
});

const fetchedProducts = ref<EcomProduct[]>([]);
const selectedItem = ref<any>(null);

const document = computed(()=> {
  return new Page({ contentData: props.page });
});

const onItemChange = (item: any) => {
  selectedItem.value = item;
};

onMounted(async() => {
  const productIds = document.value.products.map((product) => {
    return product.ecom_id.toString();
  });
  const res = await fetchProducts({ products: productIds });
  if (res) {
    fetchedProducts.value = res.products;
  }
});

const products = computed<Product[]>(() => {
  const sortedProducts = [] as Product[];
  document.value.products.forEach(fp => {
    const thisProduct = fetchedProducts.value.find(
      (p: EcomProduct) => Number(p.product) === Number(fp.ecom_id)
    );
    if (thisProduct) {
      sortedProducts.push(new Product({ contentData: null, ecomData: thisProduct }));
    }
  });
  if (sortedProducts.length) {
    return sortedProducts;
  }
  return fetchedProducts.value.map(
    (p: EcomProduct) => new Product({ contentData: null, ecomData: p })
  );
});

const swiperOptions = {
  loop: false,
  slidesPerView: 'auto',
  autoHeight: true,
  noSwiping: true,
  pagination: {
    el: '.swiper-pagination',
  },
  navigation: {
    nextEl: '.swiper-button-next',
    prevEl: '.swiper-button-prev',
  },
};

</script>

<style scoped lang="postcss">
@screen md {
  .flexBasisHalf {
    flex: 0 0 50%;
  }

  .flexBasisFull {
    flex: 0 0 100%;
  }
}

.swiper-button-next,
.swiper-button-prev {
  @apply h-40 w-40 rounded-full bg-secondary;
  display: none;

  &:after {
    @apply hidden;
  }
}

.swiper-container:hover .swiper-button-next,
.swiper-container:hover .swiper-button-prev {
  @screen md {
    display: flex;
  }
}

.swiper-pagination-bullet:only-child {
  visibility: hidden;
}

.desktopImagesMulti {
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: repeat(3, 1fr);
  grid-column-gap: 12px;
  grid-row-gap: 12px;
}

.image1 {
  grid-area: 1 / 1 / 2 / 3;
}

.image2 {
  grid-area: 2 / 1 / 3 / 2;
}

.image3 {
  grid-area: 2 / 2 / 3 / 3;
}

.image4 {
  grid-area: 3 / 1 / 4 / 2;
}

.image5 {
  grid-area: 3 / 2 / 4 / 3;
}
</style>
